import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { authApi, CachedQuery } from '../services/authApi';

interface CacheManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function formatTimeAgo(unixSeconds: number): string {
  const now = Date.now() / 1000;
  const diff = now - unixSeconds;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function formatTTL(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
}

const CacheManagerModal: React.FC<CacheManagerModalProps> = ({ isOpen, onClose }) => {
  const [queries, setQueries] = useState<CachedQuery[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'search' | 'cepi'>('search');

  const searchQueries = queries.filter(q => q.endpoint !== 'cepi');
  const cepiQueries = queries.filter(q => q.endpoint === 'cepi');
  const visibleQueries = activeTab === 'cepi' ? cepiQueries : searchQueries;
  // Only the selected keys belonging to the ACTIVE tab. The `selected` set is
  // global, so without this a selection made on the hidden tab would be deleted
  // by the active tab's "Delete Selected" and inflate its count.
  const visibleSelectedKeys = visibleQueries.filter(q => selected.has(q.key)).map(q => q.key);

  const fetchQueries = useCallback(async () => {
    setLoading(true);
    try {
      const data = await authApi.listCachedQueries();
      setQueries(data.queries);
      setSelected(new Set());
    } catch (err) {
      console.error('Failed to load cached queries:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchQueries();
      setMessage(null);
    }
  }, [isOpen, fetchQueries]);

  const toggleSelect = (key: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    const visibleKeys = visibleQueries.map(q => q.key);
    const allSelected = visibleKeys.every(k => selected.has(k)) && visibleKeys.length > 0;
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelected) visibleKeys.forEach(k => next.delete(k));
      else visibleKeys.forEach(k => next.add(k));
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    if (visibleSelectedKeys.length === 0) return;
    setDeleting(true);
    try {
      const result = await authApi.deleteCachedQueries(visibleSelectedKeys);
      setMessage(`Deleted ${result.deleted} cached ${result.deleted === 1 ? 'entry' : 'entries'}`);
      await fetchQueries();   // fetchQueries already resets `selected` to an empty set
    } catch (err) {
      console.error('Failed to delete cached queries:', err);
      setMessage('Failed to delete');
    } finally {
      setDeleting(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleClearAll = async () => {
    setClearing(true);
    try {
      if (activeTab === 'cepi') {
        const result = await authApi.clearCepiCache();
        setMessage(`Cleared ${result.deleted} CEPI answer${result.deleted === 1 ? '' : 's'}`);
      } else {
        const result = await authApi.clearCache();
        setMessage(`Cleared ${result.v2_cleared ?? result.v1_cleared ?? 0} cached queries`);
      }
      await fetchQueries();
    } catch (err) {
      console.error('Failed to clear cache:', err);
      setMessage('Failed to clear cache');
    } finally {
      setClearing(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Manage Cache</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-6 pt-3 border-b border-slate-200">
          {(['search', 'cepi'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === tab
                  ? 'border-indigo-600 text-indigo-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab === 'search' ? `Search Cache (${searchQueries.length})` : `CEPI Answers (${cepiQueries.length})`}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
              <span className="ml-3 text-sm text-slate-500">Loading cached queries...</span>
            </div>
          ) : visibleQueries.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <svg className="w-12 h-12 mx-auto mb-3 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <p className="text-sm">No cached queries found</p>
            </div>
          ) : (
            <div className="space-y-1">
              {/* Select All */}
              <label className="flex items-center gap-3 px-3 py-2 text-xs font-medium text-slate-500 uppercase tracking-wide">
                <input
                  type="checkbox"
                  checked={visibleQueries.length > 0 && visibleQueries.every(q => selected.has(q.key))}
                  onChange={toggleAll}
                  className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                />
                {visibleQueries.length} cached {visibleQueries.length === 1 ? 'query' : 'queries'}
              </label>

              {/* Query list */}
              {visibleQueries.map(q => (
                <label
                  key={q.key}
                  className={`flex items-start gap-3 px-3 py-3 rounded-lg cursor-pointer transition-colors ${
                    selected.has(q.key) ? 'bg-indigo-50 border border-indigo-200' : 'hover:bg-slate-50 border border-transparent'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(q.key)}
                    onChange={() => toggleSelect(q.key)}
                    className="mt-0.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{q.query}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                        q.endpoint === 'cepi' ? 'bg-indigo-100 text-indigo-700'
                          : q.endpoint === 'drugs' ? 'bg-blue-100 text-blue-700'
                          : 'bg-emerald-100 text-emerald-700'
                      }`}>
                        {q.endpoint === 'cepi' && q.mode ? q.mode : q.endpoint}
                      </span>
                      {q.drug_label && <span>{q.drug_label}</span>}
                      {typeof q.turn_depth === 'number' && <span>turn {q.turn_depth}</span>}
                      {q.endpoint !== 'cepi' && <span>{q.result_count} results</span>}
                      <span>{formatTimeAgo(q.cached_at)}</span>
                      <span>TTL: {formatTTL(q.ttl_remaining)}</span>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          <div className="flex items-center gap-2">
            {message && (
              <span className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">{message}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleClearAll}
              disabled={clearing || visibleQueries.length === 0}
              className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {clearing ? 'Clearing...' : 'Clear All'}
            </button>
            <button
              onClick={handleDeleteSelected}
              disabled={deleting || visibleSelectedKeys.length === 0}
              className="px-3 py-1.5 text-sm text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {deleting ? 'Deleting...' : `Delete Selected (${visibleSelectedKeys.length})`}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CacheManagerModal;
